import {
  AppReloadingLoadingType,
  articleStoreType,
  Building,
  Company,
  contextStoreType,
  NotificationType,
  storeType,
  User,
} from "@/types";
import { ReactNode } from "react";
import { create } from "zustand";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useLoggedInUserQuery } from "@/api";
import { auth } from "@/lib/firebase";
import { getBuildingId } from "@/lib";
import { getStoredCompany, storeCompany } from "./storedCompany";

// IDs stay unique even for synchronous notices and after dismissal while an
// earlier notice's timeout is still pending. Keep the existing numeric API.
let nextNotificationId = 0;

export const useSnackbarStore = create<storeType>((set) => ({
  notifications: [],

  addNotification: (
    message: string | ReactNode,
    type: NotificationType = NotificationType.ERROR,
    persistent = false,
    onClose?: () => void,
  ) => {
    const id = ++nextNotificationId;
    set(({ notifications }) => ({
      notifications: [...notifications, { id, message, type, onClose }],
    }));
    if (!persistent) {
      setTimeout(() => {
        set(({ notifications }) => ({
          notifications: notifications.filter((n) => n.id !== id),
        }));
      }, 10000); // 10 secs
    }
    return id;
  },
  removeNotification: (id: number) =>
    set(({ notifications }) => {
      notifications.find((n) => n.id === id)?.onClose?.();
      return {
        notifications: notifications.filter((n) => n.id !== id),
      };
    }),
}));

export { getStoredCompany };

export const useContextStore = create<contextStoreType>((set, get) => ({
  selectedCompany: getStoredCompany(getBuildingId()),
  buildingId: getBuildingId(),
  setCompany: (selectedCompany, buildingId = getBuildingId()) => {
    storeCompany(buildingId, selectedCompany);
    set({ selectedCompany, buildingId });
  },
  rememberCompany: (buildingId, selectedCompany) =>
    storeCompany(buildingId, selectedCompany),
  syncBuilding: (buildingId) => {
    if (get().buildingId === buildingId) return;
    set({ selectedCompany: getStoredCompany(buildingId), buildingId });
  },
}));

export const useArticleStore = create<articleStoreType>((set) => ({
  storedArticle: null,
  hasGeneratedAiContent: false,
  aiImageOptions: [],
  setStoredArticle: (storedArticle) => set({ storedArticle }),
  setHasGeneratedAiContent: (hasGeneratedAiContent) =>
    set({ hasGeneratedAiContent }),
  setAiImageOptions: (aiImageOptions) => set({ aiImageOptions }),
}));

export const useReloadApp = create<AppReloadingLoadingType>((set) => ({
  outletKey: 0,
  bumpOutletKey: () =>
    set((prev) => {
      return { outletKey: prev.outletKey + 1 };
    }),
}));

type RouterStoreType = {
  dashBoardEnabled: boolean;
  user: User;
  company: Company;
  handbookLink: string;
  queryClient: QueryClient;
  hasWelcomeFood: boolean;
  supplierCompanyId: string;
  building: Building;
  initialized: boolean;
};

export const useRouterStore = create<
  RouterStoreType & { setValues: (values: Partial<RouterStoreType>) => void }
>((set) => ({
  dashBoardEnabled: false,
  user: undefined!,
  company: undefined!,
  handbookLink: "",
  queryClient: undefined!,
  hasWelcomeFood: false,
  supplierCompanyId: undefined!,
  building: undefined!,
  initialized: false,
  setValues: (values) => set((state) => ({ ...state, ...values })),
}));

export const useSetRouterStoreValues = () => {
  const setValues = useRouterStore((state) => state.setValues);
  return setValues;
};

export const usePortalLoadingStore = create<{
  loading: boolean;
  setLoading: (loading: boolean) => void;
}>((set) => ({
  loading: false,
  setLoading: (loading: boolean) => set(() => ({ loading })),
}));

export const useLoggedInUser = (): User => {
  const { data: user } = useLoggedInUserQuery(auth.currentUser!.uid);
  // BuildingUserProvider gates children on this query having a user, so it is
  // always defined for consumers rendered inside the app.
  return user as User;
};
export const useRefetchUser = () => {
  const queryClient = useQueryClient();
  // Two entries exist: [..., "user", id, "logged-in"] (this user, throws on
  // failure) and [..., "user", id] (userOptions, used for looking up arbitrary
  // users including this one). Both must be invalidated — useSelectedCompany
  // reads adminInCompanies from the former, and a stale copy there resets a
  // just-granted company context. The shorter key is a prefix of the longer
  // one, so a single invalidation covers both.
  // Returned so callers that depend on the fresh user can await it.
  return () =>
    queryClient.invalidateQueries({
      queryKey: [getBuildingId(), "user", auth.currentUser!.uid],
    });
};
